import { Command } from "commander";
import {
	logger,
	type IAgentRuntime,
	type Character,
	type ProjectAgent,
	type Plugin,
	AgentRuntime,
	stringToUuid,
	settings
} from "@elizaos/core";
import { createDatabaseAdapter } from "@elizaos/plugin-sql";
import { loadProject } from "../project.js";
import { TestRunner } from "../testRunner.js";
import { AgentServer, type ServerOptions } from "../server/index.js";
import * as net from "node:net";
import ora from "ora";
import path from "node:path";
import * as os from "node:os";
import * as fs from "node:fs";
import * as dotenv from "dotenv";
import { jsonToCharacter, loadCharacterTryPath } from "../server/loader";
import { generateCustomCharacter } from "../utils/character-generator.js";
import {
	displayConfigStatus,
	getPluginStatus,
	loadConfig,
	saveConfig,
} from "../utils/config-manager.js";
import {
	promptForEnvVars,
	promptForServices,
} from "../utils/env-prompt.js";
import { promptForProjectPlugins } from "./start.js";

// Helper function to check port availability
async function checkPortAvailable(port: number): Promise<boolean> {
	return new Promise((resolve) => {
		const server = net.createServer();
		server.once("error", () => {
			resolve(false);
		});
		server.once("listening", () => {
			server.close();
			resolve(true);
		});
		server.listen(port);
	});
}

// Helper function to start an agent - identical to start.ts
async function startAgent(
	character: Character,
	server: AgentServer,
	init?: (runtime: IAgentRuntime) => void,
	plugins: Plugin[] = [],
	options: {
		dataDir?: string;
		postgresUrl?: string;
	} = {},
): Promise<IAgentRuntime> {
	character.id ??= stringToUuid(character.name);

	// Create runtime with server's database adapter
	const runtime = new AgentRuntime({
		character,
		plugins,
		adapter: server.database // Use server's database adapter
	});

	if (init) {
		await init(runtime);
	}

	// start services/plugins/process knowledge
	await runtime.initialize();

	// add to container
	server.registerAgent(runtime);

	// report to console
	logger.debug(`Started ${runtime.character.name} as ${runtime.agentId}`);

	return runtime;
}

const runAgentTests = async (options: {
	configure?: boolean;
	port?: number;
	character?: string;
	plugin?: string;
	skipPlugins?: boolean;
	skipProjectTests?: boolean;
}) => {
	let runtimes: IAgentRuntime[] = [];
	const projectAgents: ProjectAgent[] = [];

	// Set up standard paths and load .env
	const homeDir = os.homedir();
	const elizaDir = path.join(homeDir, ".eliza");
	const elizaDbDir = path.join(elizaDir, "db");
	const envFilePath = path.join(elizaDir, ".env");

	// Create .eliza directory if it doesn't exist
	if (!fs.existsSync(elizaDir)) {
		fs.mkdirSync(elizaDir, { recursive: true });
		logger.info(`Created directory: ${elizaDir}`);
	}

	// Create db directory if it doesn't exist
	if (!fs.existsSync(elizaDbDir)) {
		fs.mkdirSync(elizaDbDir, { recursive: true });
		logger.info(`Created database directory: ${elizaDbDir}`);
	}

	// Set the database directory in environment variables
	process.env.PGLITE_DATA_DIR = elizaDbDir;
	logger.info(`Using database directory: ${elizaDbDir}`);

	// Load environment variables from .eliza/.env if it exists
	if (fs.existsSync(envFilePath)) {
		dotenv.config({ path: envFilePath });
	}

	// Always ensure database configuration is set
	try {
		await promptForEnvVars("pglite");
	} catch (error) {
		logger.warn(`Error configuring database: ${error}`);
	}

	// Create server instance with database adapter
	const server = new AgentServer({
		dataDir: elizaDbDir,
		postgresUrl: process.env.POSTGRES_URL,
	});

	// Set up server properties
	server.startAgent = async (character) => {
		logger.info(`Starting agent for character ${character.name}`);
		return startAgent(character, server);
	};
	server.loadCharacterTryPath = loadCharacterTryPath;
	server.jsonToCharacter = jsonToCharacter;

	const serverPort = Number.parseInt(options.port?.toString() || "3000", 10);

	// Start the server first to ensure database is initialized
	let currentPort = serverPort;
	while (!(await checkPortAvailable(currentPort))) {
		logger.warn(`Port ${currentPort} is in use, trying ${currentPort + 1}`);
		currentPort++;
	}

	await server.start(currentPort);
	logger.success(`Test server started on port ${currentPort}`);

	try {
		const spinner = ora("Starting agent...").start();
		
		// Start the agent runtime(s)
		if (options.character) {
			const character = await server.loadCharacterTryPath(options.character);
			const runtime = await server.startAgent(character);
			runtimes = [runtime];
		} else {
			try {
				// Look for project or plugin in current directory
				logger.info("Checking for project or plugin in current directory...");
				const currentDir = process.cwd();
				logger.info(`Current directory: ${currentDir}`);

				let isProject = false;
				const isPlugin = false;
				let projectModule: any = null;

				// Check if we're in a project with a package.json
				const packageJsonPath = path.join(process.cwd(), "package.json");
				if (fs.existsSync(packageJsonPath)) {
					const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, "utf-8"));

					if (packageJson.eliza?.type === "project") {
						isProject = true;
						logger.info("Found Eliza project in current directory");
					}

					// Also check for project indicators
					if (!isProject && packageJson.description?.toLowerCase().includes("project")) {
						isProject = true;
						logger.info("Found project by description in package.json");
					}

					// If we found a main entry in package.json, try to load it
					const mainEntry = packageJson.main;
					if (mainEntry) {
						const mainPath = path.resolve(process.cwd(), mainEntry);
						if (fs.existsSync(mainPath)) {
							try {
								projectModule = await import(mainPath);
								if (projectModule.default?.agents) {
									isProject = true;
									logger.info(`Loaded project with ${projectModule.default.agents.length} agents`);
								}
							} catch (importError) {
								logger.error(`Error importing module: ${importError}`);
							}
						}
					}
				}

				// Look for specific project files if no project found yet
				if (!isProject) {
					const projectFiles = ["project.json", "eliza.json", "agents.json"];
					for (const file of projectFiles) {
						const filePath = path.join(process.cwd(), file);
						if (fs.existsSync(filePath)) {
							try {
								const fileContent = fs.readFileSync(filePath, "utf-8");
								const projectData = JSON.parse(fileContent);
								if (projectData.agents || projectData.agent) {
									isProject = true;
									projectModule = { default: projectData };
									logger.info(`Found project in ${file}`);
									break;
								}
							} catch (error) {
								logger.warn(`Error reading possible project file ${file}: ${error}`);
							}
						}
					}
				}

				if (isProject && projectModule?.default) {
					const project = projectModule.default;
					const agents = Array.isArray(project.agents)
						? project.agents
						: project.agent
							? [project.agent]
							: [];

					if (agents.length > 0) {
						logger.info(`Found ${agents.length} agents in project`);

						// Prompt for environment variables for all plugins in the project
						try {
							await promptForProjectPlugins(project);
						} catch (error) {
							logger.warn(`Failed to prompt for project environment variables: ${error}`);
						}

						for (const agent of agents) {
							logger.info(`Starting agent: ${agent.character.name}`);
							const agentPlugins = agent.plugins || [];
							logger.info(`Found ${agentPlugins.length} plugins to test for agent ${agent.character.name}`);
							
							try {
								const runtime = await startAgent(
									agent.character,
									server,
									agent.init,
									agentPlugins
								);
								runtimes.push(runtime);
								projectAgents.push(agent);
								await new Promise((resolve) => setTimeout(resolve, 500));
							} catch (agentError) {
								logger.error(`Error starting agent ${agent.character.name}: ${agentError}`);
							}
						}

						if (runtimes.length === 0) {
							throw new Error("Failed to start any agents from project");
						}
						
						logger.success(`Successfully started ${runtimes.length} agents from project`);
					} else {
						throw new Error("No agents found in project configuration");
					}
				} else {
					throw new Error("No project configuration found");
				}
			} catch (error) {
				logger.error("Failed to load project:", error);
				if (error instanceof Error) {
					logger.error("Error details:", error.message);
					logger.error("Stack trace:", error.stack);
				}
				await server.stop();
				process.exit(1);
			}
		}

		spinner.succeed("Agent(s) started");

		// Run tests for each agent
		let totalFailed = 0;
		for (let i = 0; i < runtimes.length; i++) {
			const runtime = runtimes[i];
			const projectAgent = projectAgents[i];
			
			logger.info(`Running tests for agent: ${runtime.character.name}`);
			const testRunner = new TestRunner(runtime, projectAgent);
			const results = await testRunner.runTests({
				filter: options.plugin,
				skipPlugins: options.skipPlugins,
				skipProjectTests: options.skipProjectTests
			});
			totalFailed += results.failed;
		}

		// Clean up
		await server.stop();
		process.exit(totalFailed > 0 ? 1 : 0);
	} catch (error) {
		logger.error("Error running tests:", error);
		if (error instanceof Error) {
			logger.error("Error details:", error.message);
			logger.error("Stack trace:", error.stack);
		}
		await server.stop();
		process.exit(1);
	}
};

const test = new Command("test")
	.description("Run tests for Eliza agent plugins")
	.option("-c, --configure", "Configure test settings")
	.option("-p, --port <number>", "Port to run test server on", "3000")
	.option("-C, --character <path>", "Path to character file")
	.option("-P, --plugin <name>", "Name of plugin to test")
	.option("--skip-plugins", "Skip plugin tests")
	.option("--skip-project-tests", "Skip project tests")
	.action(async (options) => {
		await runAgentTests(options);
	});

export { test };
export default test;